import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Box, 
  TextField, 
  Button, 
  Typography, 
  Paper, 
  Link, 
  Divider, 
  Alert,
  CircularProgress 
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useDomain, useVisualTheme, useBranding, useLocalization } from '../context/DomainContext';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  
  const { login, googleLogin, user, userProfile } = useAuth();
  
  // Hook per Visual Theme
  const visualTheme = useVisualTheme();
  const { brandName, logoUrl, primaryColor } = useBranding();
  const { getText } = useLocalization();
  const { config, loading: domainLoading } = useDomain();

  // Reindirizzamento se già autenticato con profilo
  useEffect(() => {
    if (user && userProfile) {
      const redirectTo = location.state?.from?.pathname || '/';
      navigate(redirectTo, { replace: true });
    }
  }, [user, userProfile, navigate, location]);

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      setError('Tutti i campi sono obbligatori.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await login(email, password);
      console.log('Login result:', result);
      
      if (result.profile) {
        // Utente ha un profilo per questo progetto
        let redirectTo = location.state?.from?.pathname || '/';
        
        // Se onboarding non completato, forza redirect a onboarding
        if (!result.profile.is_onboarding_completed) {
          redirectTo = '/onboarding';
        }
        
        navigate(redirectTo, { replace: true });
      } else if (result.user) {
        // Utente autenticato ma senza profilo per questo progetto
        setError(`Non sei registrato per ${brandName || config.project_name}. Vai alla registrazione per creare un account per questo progetto.`);
        // Opzionalmente potresti fare un redirect automatico
        setTimeout(() => {
          navigate('/register');
        }, 3000);
      }
    } catch (err) {
      console.error('Login failed:', err);
      
      if (err.code === 'auth/user-not-found') {
        setError('Account non trovato. Crea un nuovo account.');
      } else if (err.code === 'auth/wrong-password') {
        setError('Password non corretta. Riprova.');
      } else if (err.code === 'auth/invalid-email') {
        setError('Indirizzo email non valido.');
      } else if (err.code === 'auth/user-disabled') {
        setError('Account disabilitato. Contatta il supporto.');
      } else if (err.code === 'auth/too-many-requests') {
        setError('Troppi tentativi. Riprova più tardi.');
      } else if (err.message) {
        setError(err.message);
      } else {
        setError(getText('error_invalid_credentials', 'Email o password non corretti. Riprova.'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setLoading(true);
    setError('');

    try {
      const result = await googleLogin();
      console.log('Google login result:', result);
      
      if (result.profile) {
        // Utente ha un profilo (nuovo o esistente)
        let redirectTo = location.state?.from?.pathname || '/';
        
        // Se onboarding non completato, forza redirect a onboarding
        if (!result.profile.is_onboarding_completed) {
          redirectTo = '/onboarding';
        }
        
        navigate(redirectTo, { replace: true });
      } else if (result.user) {
        // Questo non dovrebbe succedere con Google perché fa auto-registrazione
        // Ma gestiamolo comunque
        navigate('/onboarding', { replace: true });
      }
    } catch (err) {
      console.error('Google login failed:', err);
      
      if (err.code === 'auth/popup-closed-by-user') {
        setError('Login annullato. Riprova.');
      } else if (err.code === 'auth/popup-blocked') {
        setError('Popup bloccato dal browser. Abilita i popup e riprova.');
      } else if (err.code === 'auth/cancelled-popup-request') {
        setError('Richiesta di login annullata.');
      } else if (err.message) {
        setError(err.message);
      } else {
        setError('Errore durante il login con Google. Riprova.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (domainLoading) {
    return (
      <Container maxWidth="sm">
        <Box 
          sx={{ 
            minHeight: '100vh', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center' 
          }}
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Box 
        sx={{ 
          minHeight: '100vh', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          backgroundColor: visualTheme.primary_colors?.background || '#f8f9fa'
        }}
      >
        <Paper 
          elevation={3} 
          sx={{ 
            p: 4, 
            width: '100%', 
            maxWidth: 400,
            backgroundColor: visualTheme.primary_colors?.header || '#ffffff',
            borderRadius: visualTheme.component_styles?.button?.border_radius || '8px'
          }}
        >
          {/* Logo dinamico */}
          {logoUrl && (
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <img 
                src={logoUrl} 
                alt={`${brandName} Logo`}
                style={{ 
                  maxHeight: '80px', 
                  maxWidth: '200px', 
                  height: 'auto' 
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </Box>
          )}

          {/* Titolo con testo personalizzato */}
          <Typography 
            variant="h4" 
            component="h1" 
            gutterBottom 
            align="center"
            sx={{ 
              mb: 3,
              color: visualTheme.text_colors?.primary || '#212529',
              fontFamily: visualTheme.font_definitions?.primary_font || 'Inter, sans-serif'
            }}
          >
            {getText('page_title_login', 'Accedi al tuo account')}
          </Typography>

          {/* Sottotitolo con brand name */}
          <Typography 
            variant="body2" 
            align="center" 
            sx={{ 
              mb: 4,
              color: visualTheme.text_colors?.secondary || '#6c757d'
            }}
          >
            Benvenuto su {brandName || config.project_name}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleEmailLogin}>
            <TextField
              fullWidth
              type="email"
              label={getText('label_email', 'Indirizzo Email')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              margin="normal"
              required
              autoComplete="email"
              autoFocus
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused fieldset': {
                    borderColor: visualTheme.secondary_colors?.accent || '#0d6efd',
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: visualTheme.secondary_colors?.accent || '#0d6efd',
                }
              }}
            />

            <TextField
              fullWidth
              type="password"
              label={getText('label_password', 'Password')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              required
              autoComplete="current-password"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused fieldset': {
                    borderColor: visualTheme.secondary_colors?.accent || '#0d6efd',
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: visualTheme.secondary_colors?.accent || '#0d6efd',
                }
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{ 
                mt: 3, 
                mb: 2,
                backgroundColor: visualTheme.secondary_colors?.accent || primaryColor || '#0d6efd',
                borderRadius: visualTheme.component_styles?.button?.border_radius || '4px',
                fontFamily: visualTheme.font_definitions?.primary_font || 'Inter, sans-serif',
                '&:hover': {
                  backgroundColor: visualTheme.secondary_colors?.accent || primaryColor || '#0d6efd',
                  opacity: 0.9
                }
              }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                getText('button_login', 'Entra')
              )}
            </Button>
          </form>

          {/* Link password dimenticata */}
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Link 
              href="#" 
              variant="body2"
              onClick={(e) => {
                e.preventDefault();
                // TODO: Implementare recupero password
                setError('Funzionalità in sviluppo');
              }}
              sx={{ 
                color: visualTheme.secondary_colors?.link || '#0d6efd',
                textDecoration: 'none',
                cursor: 'pointer',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              {getText('link_forgot_password', 'Password dimenticata?')}
            </Link>
          </Box>

          <Divider sx={{ my: 2 }}>
            <Typography 
              variant="body2" 
              sx={{ 
                color: visualTheme.text_colors?.secondary || '#6c757d' 
              }}
            >
              {getText('text_divider_or', 'oppure')}
            </Typography>
          </Divider>

          {/* Bottone Google con styling del tema */}
          <Button
            fullWidth
            variant="outlined"
            startIcon={<GoogleIcon />}
            onClick={handleGoogleLogin}
            disabled={loading}
            sx={{ 
              mb: 2,
              borderColor: visualTheme.secondary_colors?.accent || '#0d6efd',
              color: visualTheme.secondary_colors?.accent || '#0d6efd',
              borderRadius: visualTheme.component_styles?.button?.border_radius || '4px',
              fontFamily: visualTheme.font_definitions?.primary_font || 'Inter, sans-serif',
              '&:hover': {
                borderColor: visualTheme.secondary_colors?.accent || '#0d6efd',
                backgroundColor: `${visualTheme.secondary_colors?.accent || '#0d6efd'}10`
              }
            }}
          >
            {getText('button_sso_google', 'Accedi con Google')}
          </Button>

          {/* Link di registrazione */}
          <Box sx={{ textAlign: 'center' }}>
            <Typography 
              variant="body2" 
              sx={{ 
                color: visualTheme.text_colors?.secondary || '#6c757d' 
              }}
            >
              {getText('prompt_no_account', 'Non hai un account?')}{' '}
              <Link 
                component="button"
                type="button"
                variant="body2"
                onClick={() => navigate('/register')}
                sx={{ 
                  color: visualTheme.secondary_colors?.link || '#0d6efd',
                  textDecoration: 'none',
                  cursor: 'pointer',
                  '&:hover': {
                    textDecoration: 'underline'
                  }
                }}
              >
                {getText('link_register_now', 'Registrati ora')}
              </Link>
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Login;